import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../../../contexts/RequestsContext";
import { useSnackbar } from "../../../../../contexts/SnackbarContext";
import { useHandleNurseChange } from "../../../../../hooks/useHandleNurseChange";
import { trackEvent } from "../../../../../utils/analytics";
import { type Nurse, type NurseInShift, NurseInShiftCreationSource, NurseTrainingStatus } from "../../../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../../../utils/utilFunctions";

type Props = {
	selectedDate: DateTime;
	onCloseCalled?: () => void;
};

/**
 * Used to post single shift for specific nurse
 * @param selectedDate
 * @param providedNurse
 * @param onCloseCalled
 *
 * `addShift` resolves the shift it created — the client-built `NurseInShift` exactly as it was written
 * and added to `hospitalData`; its uuid is the id the backend stores — so a caller can act on it next
 * (e.g. move it to a unit). It resolves `false` whenever nothing was added, so a truthiness check still
 * reads as "added".
 */
export const useAddSingleShiftByDateAndNurse = ({ selectedDate, onCloseCalled }: Props) => {
	const { refreshRequests } = useRequestsContext();
	const { hospitalData, chosenDepartment, getWeekIndexByDate, getDayIndexByDate, timezone, prepareDataForAddNurse, addNurseToShift, showErrorSnackbar } =
		useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { addNurse, isLoading: isLoadingTypes } = useHandleNurseChange();
	const { addNurse: isLoading } = isLoadingTypes;

	const addShift = useCallback(
		async (
			providedNurse: Nurse | undefined | null,
			startTime: DateTime,
			endTime: DateTime,
			selectedShiftLocation: string | undefined,
			makeCharge?: boolean,
			makeTrainee?: boolean,
			jobId?: string,
		): Promise<NurseInShift | false> => {
			if (isLoading || !providedNurse) {
				return false;
			}
			const shiftIndex = getShiftIndexByShiftTimes({
				startTime,
				endTime,
			});
			const showActual = false;
			const shiftTimesAndType = {
				start: startTime.setZone(timezone),
				end: endTime.setZone(timezone),
				date: selectedDate,
				shiftType: chosenDepartment.shift_types[shiftIndex],
			};
			const weekIndex = getWeekIndexByDate(selectedDate);
			const dayIndex = getDayIndexByDate(selectedDate);
			const nurse = prepareDataForAddNurse(
				hospitalData,
				weekIndex,
				dayIndex,
				shiftIndex,
				providedNurse.id,
				selectedShiftLocation,
				false,
				undefined,
				shiftTimesAndType,
			);

			try {
				if (!nurse) {
					return false;
				}
				const resolvedJobName = jobId ? (providedNurse.jobs?.find((j) => j.id === jobId)?.displayName ?? "") : undefined;
				const createdNurseInShift: NurseInShift = {
					...nurse,
					...(makeCharge !== undefined && { charge: makeCharge }),
					...(makeTrainee && { trainingStatus: NurseTrainingStatus.Trainee }),
					...(jobId && { jobId, jobName: resolvedJobName }),
				};
				await addNurse({
					nurse: createdNurseInShift,
					shiftIndex,
					dayNo: dayIndex,
					weekIndex,
					// The clicked date, stated rather than derived: this grid shows weeks the staffing
					// data isn't loaded for, so weekIndex is -1 there and the getShiftDate fallback
					// indexes an undefined week. We already know the date the user picked.
					date: selectedDate,
					location: selectedShiftLocation,
					isActual: showActual,
					source: NurseInShiftCreationSource.AddNurse,
				});
				addNurseToShift(createdNurseInShift, weekIndex, dayIndex, shiftIndex, showActual);
				trackEvent("Staffing - add nurse", {
					source: "add_nurse_dialog",
					week_index: weekIndex,
					day_no: dayIndex,
					shift_no: shiftIndex,
					date: selectedDate.toFormat("MM/DD/yyyy"),
					nurse_id: providedNurse.id,
					nurse_name: `${providedNurse.firstName} ${providedNurse.lastName}`,
					role: providedNurse.role,
					location: selectedShiftLocation,
				});
				showSnackbar(
					`${createdNurseInShift?.name} was successfully added from ${createdNurseInShift?.startTime.toFormat("ccc")} ${createdNurseInShift?.shiftType} shift`,
					"success",
				);
				onCloseCalled?.();
				await refreshRequests();
				return createdNurseInShift;
			} catch (e) {
				// Prefer the backend's own reason over a generic failure: an overlap rejection
				// (error.shift.already.exists) is a translated, actionable message, and showing
				// "Failed to add nurse" instead makes a legitimate refusal look like a broken button.
				const detail = (e as { response?: { data?: { detail?: string | { message?: string } } } })?.response?.data?.detail;
				const message = (typeof detail === "string" ? detail : detail?.message) || "Failed to add nurse";
				showErrorSnackbar(message, { nurse }, e);
				return false;
			}
		},
		[
			addNurse,
			chosenDepartment,
			isLoading,
			refreshRequests,
			timezone,
			showSnackbar,
			showErrorSnackbar,
			selectedDate.toFormat,
			prepareDataForAddNurse,
			selectedDate,
			onCloseCalled,
			hospitalData,
			getWeekIndexByDate,
			addNurseToShift,
			getDayIndexByDate,
		],
	);

	return {
		addShift,
		isLoading,
	};
};
